import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Mail, Send, MapPin, Phone, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export default function Contact() {
  const { currentColors } = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="glass-card p-12 text-center">
          <CheckCircle className="w-20 h-20 mx-auto mb-6" style={{ color: '#22c55e' }} />
          <h2 className="text-2xl font-cinzel mb-4" style={{ color: currentColors.primary }}>
            Message Sent Successfully!
          </h2>
          <p className="mb-6" style={{ color: currentColors.text }}>
            Thank you for reaching out. We'll get back to you as soon as possible.
          </p>
          <button
            onClick={() => setSuccess(false)}
            className="px-6 py-2 rounded-xl"
            style={{ backgroundColor: currentColors.primary, color: 'white' }}
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="contact-page">
      {/* Header */}
      <div className="glass-card p-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 sacred-geometry opacity-20"></div>
        <div className="relative z-10">
          <Mail className="w-12 h-12 mx-auto mb-4" style={{ color: currentColors.primary }} />
          <h1 className="text-3xl font-cinzel mb-2" style={{ color: currentColors.primary }}>
            Contact Us
          </h1>
          <p style={{ color: currentColors.textMuted }}>
            We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Contact Form */}
        <div className="lg:col-span-2">
          <div className="glass-card p-6">
            <h2 className="text-xl font-cinzel mb-6" style={{ color: currentColors.text }}>
              Send us a Message
            </h2>
            
            {error && (
              <div 
                className="p-4 rounded-xl mb-6 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
              >
                <AlertCircle className="w-5 h-5 text-red-500" />
                <span className="text-red-500">{error}</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    className="w-full cosmic-input"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    className="w-full cosmic-input"
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Subject *
                </label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  placeholder="How can we help you?"
                  className="w-full cosmic-input"
                  data-testid="contact-subject-input"
                />
              </div>
              
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Message *
                </label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  placeholder="Tell us more about your inquiry..."
                  rows={6}
                  className="w-full cosmic-input resize-none"
                  data-testid="contact-message-input"
                />
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ backgroundColor: currentColors.primary, color: 'white' }}
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Contact Info */}
        <div className="space-y-6">
          <div className="glass-card p-6">
            <h3 className="font-cinzel mb-4" style={{ color: currentColors.text }}>Get in Touch</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-1" style={{ color: currentColors.primary }} />
                <div>
                  <p className="font-medium" style={{ color: currentColors.text }}>Email</p>
                  <p style={{ color: currentColors.textMuted }}>support@theaura-space.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 mt-1" style={{ color: currentColors.primary }} />
                <div>
                  <p className="font-medium" style={{ color: currentColors.text }}>Response Time</p>
                  <p style={{ color: currentColors.textMuted }}>Usually within 24 hours</p>
                </div>
              </div>
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-cinzel mb-4" style={{ color: currentColors.text }}>Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a href="/articles" className="flex items-center gap-2 hover:underline" style={{ color: currentColors.primary }}>
                  <span>FAQ & Help Articles</span>
                </a>
              </li>
              <li>
                <a href="/community" className="flex items-center gap-2 hover:underline" style={{ color: currentColors.primary }}>
                  <span>Community Forum</span>
                </a>
              </li>
              <li>
                <a href="/sessions" className="flex items-center gap-2 hover:underline" style={{ color: currentColors.primary }}>
                  <span>Book a Session</span>
                </a>
              </li>
            </ul>
          </div>

          <div 
            className="glass-card p-6"
            style={{ 
              background: `linear-gradient(135deg, ${currentColors.primary}10, ${currentColors.secondary}10)`,
              border: `1px solid ${currentColors.primary}30`
            }}
          >
            <h3 className="font-cinzel mb-2" style={{ color: currentColors.primary }}>Need Immediate Help?</h3>
            <p className="text-sm mb-3" style={{ color: currentColors.textMuted }}>
              Our support team is available through the chat widget at the bottom right of your screen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
